import { useState, type ComponentType } from 'react';

import { Icon } from '@/components/ui';
import { cx } from '@/components/ui/classNames';

import { AdminCategories } from './AdminCategories';
import { AdminConfiguration } from './AdminConfiguration';
import { AdminGroups } from './AdminGroups';
import { AdminGruppenkennzeichen } from './AdminGruppenkennzeichen';
import { AdminUsers } from './AdminUsers';
import { AdminViews } from './AdminViews';

/** Bereiche der Administration — Nachfolger von `IwmAdmin.IwmAdminPageTypes`. */
export type AdminSectionId =
  'users' | 'permissions' | 'views' | 'categories' | 'groupFlags' | 'settings';

export interface AdminPanelProps {
  /** Nur für Tests; sonst beginnt die Administration bei „Users". */
  initialSection?: AdminSectionId;
}

interface AdminSection {
  id: AdminSectionId;
  label: string;
  iconClass: string;
  Section: ComponentType;
}

/**
 * Reihenfolge und Beschriftungen wie in `IwmAdmin.razor` (REQ-25 §4.2). Der Altclient
 * wechselte über ein `switch`; als Tabelle ist ein neuer Bereich eine Zeile. Die Symbole
 * ersetzen das für fünf Bereiche gleiche `e-settings`.
 */
const SECTIONS: readonly AdminSection[] = [
  { id: 'users', label: 'Users', iconClass: 'fa-solid fa-users', Section: AdminUsers },
  {
    id: 'permissions',
    label: 'Permissions',
    iconClass: 'fa-solid fa-shield-halved',
    Section: AdminGroups,
  },
  { id: 'views', label: 'Views', iconClass: 'fa-solid fa-table', Section: AdminViews },
  {
    id: 'categories',
    label: 'Categories',
    iconClass: 'fa-solid fa-folder',
    Section: AdminCategories,
  },
  {
    id: 'groupFlags',
    label: 'Group flags',
    iconClass: 'fa-solid fa-tags',
    Section: AdminGruppenkennzeichen,
  },
  { id: 'settings', label: 'Settings', iconClass: 'fa-solid fa-gear', Section: AdminConfiguration },
];

/** Inhalt der Administrationsschublade: Bereichsmenü links, Abschnitt rechts. */
export function AdminPanel(props: AdminPanelProps) {
  const [selected, setSelected] = useState<AdminSectionId>(props.initialSection ?? 'users');
  const Section = SECTIONS.find((section) => section.id === selected)?.Section;

  return (
    <div className="iwm-admin-panel">
      <nav className="iwm-admin-nav" aria-label="Administrationsbereiche">
        {SECTIONS.map((section) => {
          const active = section.id === selected;
          return (
            <button
              key={section.id}
              type="button"
              aria-current={active ? 'page' : undefined}
              className={cx('iwm-admin-nav-item', active && 'iwm-admin-nav-item--active')}
              onClick={() => {
                setSelected(section.id);
              }}
            >
              <Icon iconClass={section.iconClass} />
              <span>{section.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="iwm-admin-section">{Section === undefined ? null : <Section />}</div>
    </div>
  );
}

export default AdminPanel;
